import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, CheckCircle2, Edit2, Loader2 } from 'lucide-react';
import { DetectedPolicy } from '@/hooks/useMultiTypeDetection';

interface MultiTypeDetectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detectedPolicies: DetectedPolicy[];
  onConfirm: (selectedPolicies: DetectedPolicy[]) => Promise<void>;
  isSubmitting: boolean;
}

export function MultiTypeDetectionDialog({
  open,
  onOpenChange,
  detectedPolicies,
  onConfirm,
  isSubmitting,
}: MultiTypeDetectionDialogProps) {
  const [policies, setPolicies] = useState<DetectedPolicy[]>(detectedPolicies);
  const [editingId, setEditingId] = useState<string | null>(null);

  const togglePolicy = (index: number) => {
    setPolicies(prev => prev.map((p, i) => 
      i === index ? { ...p, selected: !p.selected } : p
    ));
  };

  const toggleAll = () => {
    const allSelected = policies.every(p => p.selected);
    setPolicies(prev => prev.map(p => ({ ...p, selected: !allSelected })));
  };

  const updatePolicy = (index: number, field: keyof DetectedPolicy, value: any) => {
    setPolicies(prev => prev.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 dark:text-green-400';
    if (confidence >= 0.7) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.9) return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">High</Badge>;
    if (confidence >= 0.7) return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Medium</Badge>;
    return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Low</Badge>;
  };

  const selectedCount = policies.filter(p => p.selected).length;
  const allSelected = policies.every(p => p.selected);

  const handleConfirm = async () => {
    const selected = policies.filter(p => p.selected);
    if (selected.length === 0) {
      return;
    }
    await onConfirm(selected);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Multiple Policies Detected
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Found {detectedPolicies.length} {detectedPolicies.length === 1 ? 'policy' : 'policies'} in this document. 
            Review and select which ones to save.
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="space-y-4">
            {policies.map((policy, index) => (
              <div 
                key={index} 
                className={`border rounded-lg p-4 transition-colors ${
                  policy.selected 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border bg-muted/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={policy.selected}
                    onCheckedChange={() => togglePolicy(index)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-semibold">{policy.document_type_name}</h4>
                        <p className="text-xs text-muted-foreground">ID: {policy.document_type_id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getConfidenceBadge(policy.confidence)}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingId(editingId === policy.document_type_id ? null : policy.document_type_id)}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {editingId === policy.document_type_id ? (
                      <div className="space-y-3 bg-background p-3 rounded border">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Policy Number</Label>
                            <Input
                              value={policy.document_number || ''}
                              onChange={(e) => updatePolicy(index, 'document_number', e.target.value)}
                              placeholder="Enter policy number"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Coverage Amount</Label>
                            <Input
                              value={policy.coverage_amount || ''}
                              onChange={(e) => updatePolicy(index, 'coverage_amount', e.target.value)}
                              placeholder="e.g., $1,000,000"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Issue Date</Label>
                            <Input
                              type="date"
                              value={policy.issue_date || ''}
                              onChange={(e) => updatePolicy(index, 'issue_date', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Expiry Date</Label>
                            <Input
                              type="date"
                              value={policy.expiry_date || ''}
                              onChange={(e) => updatePolicy(index, 'expiry_date', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Issuing Authority</Label>
                            <Input
                              value={policy.issuing_authority || ''}
                              onChange={(e) => updatePolicy(index, 'issuing_authority', e.target.value)}
                              placeholder="Enter issuing authority"
                              className="h-8"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        {policy.document_number && (
                          <div>
                            <span className="text-muted-foreground">Policy:</span>
                            <span className="ml-2 font-medium">{policy.document_number}</span>
                          </div>
                        )}
                        {policy.coverage_amount && (
                          <div>
                            <span className="text-muted-foreground">Coverage:</span>
                            <span className="ml-2 font-medium">{policy.coverage_amount}</span>
                          </div>
                        )}
                        {policy.issue_date && (
                          <div>
                            <span className="text-muted-foreground">Issue:</span>
                            <span className="ml-2 font-medium">{new Date(policy.issue_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {policy.expiry_date && (
                          <div>
                            <span className="text-muted-foreground">Expires:</span>
                            <span className="ml-2 font-medium">{new Date(policy.expiry_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {policy.issuing_authority && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Authority:</span>
                            <span className="ml-2 font-medium">{policy.issuing_authority}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {policy.confidence < 0.7 && (
                      <div className="flex items-start gap-2 text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/20 p-2 rounded">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>Low confidence detection. Please verify the extracted data.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Button variant="outline" size="sm" onClick={toggleAll}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <span>
              {selectedCount} of {policies.length} selected
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={isSubmitting || selectedCount === 0}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                `Upload ${selectedCount} ${selectedCount === 1 ? 'Document' : 'Documents'}`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
